import { AFFILIATE_CARRIERS, getFallbackCarrier, type AffiliateCarrier } from '@/data/affiliateCarriers';

interface UserProfile {
  name: string;
  age: number;
  state: string;
  coverageAmount: string;
  smokingStatus: string;
  policyType?: string;
  healthStatus?: string;
  healthConditions?: string;
  priority?: string;
  noMedicalExam?: string;
  approvalPreference?: string;
}

export class CarrierMatchingEngine {
  private carriers: AffiliateCarrier[];

  constructor(customCarriers?: AffiliateCarrier[]) {
    this.carriers = customCarriers || AFFILIATE_CARRIERS;
  }

  matchCarriers(profile: UserProfile): { carrier: AffiliateCarrier; score: number; reasons: string[] }[] {
    const matches: { carrier: AffiliateCarrier; score: number; reasons: string[] }[] = [];

    for (const carrier of this.carriers) {
      const evaluation = this.evaluateCarrier(carrier, profile);
      if (evaluation.score > 0) {
        matches.push(evaluation);
      }
    }

    // Sort by score (highest first)
    return matches.sort((a, b) => b.score - a.score);
  }

  getBestRecommendation(profile: UserProfile): { carrier: AffiliateCarrier; score: number; reasons: string[] } | null {
    const matches = this.matchCarriers(profile);
    
    // Check if Ladder is available and should be prioritized
    const ladderMatch = matches.find(m => m.carrier.id === 'ladder-life');
    if (ladderMatch && ladderMatch.carrier.isPrimary) {
      // Force Ladder as the top recommendation if it's available
      return ladderMatch;
    }
    
    // If no matches found, default to fallback carrier
    if (matches.length === 0) {
      const fallbackCarrier = getFallbackCarrier();
      return {
        carrier: fallbackCarrier,
        score: 50, // Default fallback score
        reasons: ['Our recommended partner for your profile', 'Flexible coverage options', 'Competitive rates']
      };
    }
    
    return matches.length > 0 ? matches[0] : null;
  }

  // Helper function to compute display match percentage
  static computeMatchPercent(score: number, carrierId: string): number {
    if (carrierId === 'ladder-life') {
      // Ladder gets 92-99% range
      return Math.max(92, Math.min(99, 90 + score * 0.08));
    } else {
      // Other carriers get 90%+ but cap at reasonable levels
      return Math.max(90, Math.min(95, 85 + score * 0.1));
    }
  }

  private evaluateCarrier(carrier: AffiliateCarrier, profile: UserProfile): { carrier: AffiliateCarrier; score: number; reasons: string[] } {
    let score = 0;
    const reasons: string[] = [];
    const conditions = carrier.conditions;
    const scoring = carrier.scoring;

    // Check basic eligibility
    if (!this.meetsBasicRequirements(carrier, profile)) {
      return { carrier, score: 0, reasons: ['Does not meet basic requirements'] };
    }

    // Base score + any score boost
    score += scoring.baseScore;
    if (scoring.scoreBoost) {
      score += scoring.scoreBoost;
    }
    reasons.push(`Base compatibility: ${scoring.baseScore} points`);

    // Age-based scoring
    if (scoring.ageBonus) {
      for (const ageRule of scoring.ageBonus) {
        if (profile.age >= ageRule.min && profile.age <= ageRule.max) {
          score += ageRule.bonus;
          reasons.push(`Age group bonus (${ageRule.min}-${ageRule.max}): +${ageRule.bonus} points`);
          break;
        }
      }
    }

    // Health status bonus
    if (scoring.healthBonus && profile.healthStatus) {
      const healthBonus = scoring.healthBonus.find(h => h.status === profile.healthStatus);
      if (healthBonus) {
        score += healthBonus.bonus;
        reasons.push(`Health status (${profile.healthStatus}): +${healthBonus.bonus} points`);
      }
    }

    // Priority matching
    if (scoring.priorityMatch && profile.priority) {
      const priorityBonus = scoring.priorityMatch.find(p => p.priority === profile.priority);
      if (priorityBonus) {
        score += priorityBonus.bonus;
        reasons.push(`Priority match (${profile.priority}): +${priorityBonus.bonus} points`);
      }
    }

    // No medical exam preference
    if (profile.noMedicalExam === 'yes' && conditions.noMedicalExamAvailable && scoring.noMedicalExamBonus) {
      score += scoring.noMedicalExamBonus;
      reasons.push(`No medical exam available: +${scoring.noMedicalExamBonus} points`);
    }

    // Instant approval preference
    if (profile.approvalPreference === 'instant' && 
        carrier.conditions.specialties?.includes('fast_approval') && 
        scoring.instantApprovalBonus) {
      score += scoring.instantApprovalBonus;
      reasons.push(`Fast approval available: +${scoring.instantApprovalBonus} points`);
    }

    // Smoking status bonus (smoker-friendly carriers get bonus for smokers)
    if (profile.smokingStatus === 'yes' && 
        carrier.conditions.specialties?.includes('smoker_friendly')) {
      score += 20;
      reasons.push('Smoker-friendly carrier: +20 points');
    }

    return { carrier, score, reasons };
  }

  private meetsBasicRequirements(carrier: AffiliateCarrier, profile: UserProfile): boolean {
    const conditions = carrier.conditions;

    // Age requirements
    if (conditions.ageMin && profile.age < conditions.ageMin) return false;
    if (conditions.ageMax && profile.age > conditions.ageMax) return false;

    // Smoking requirements
    if (!conditions.smokingAllowed && profile.smokingStatus === 'yes') return false;

    // Health status requirements
    if (conditions.healthStatusRequired && profile.healthStatus && 
        !conditions.healthStatusRequired.includes(profile.healthStatus)) return false;

    // Policy type requirements
    if (conditions.policyTypesOffered && profile.policyType && 
        !conditions.policyTypesOffered.includes(profile.policyType)) return false;

    // State availability (placeholder - would need real state data)
    if (conditions.statesAvailable && profile.state && 
        !conditions.statesAvailable.includes(profile.state)) return false;

    return true;
  }

  // Method to update carriers with real data later
  updateCarriers(newCarriers: AffiliateCarrier[]) {
    this.carriers = newCarriers;
  }

  // Get all available carriers for admin/debug purposes
  getAllCarriers(): AffiliateCarrier[] {
    return this.carriers;
  }
}

export type { UserProfile };